/**
 * SERVICIO DE TESTS (TEST A, TEST B, TEST C y TEST D)
 * 
 * Este módulo proporciona funcionalidades para enviar respuestas de los tests
 * al backend de FastAPI. Gestiona la comunicación con los endpoints REST para:
 * - Test A (test Cleaver)
 * - Test B (test de valores)
 * - Test C (test de canciones)
 * - Test D (test de dibujos)
 * 
 * @author Ana Lizcano
 * @version 2.2.0
 */

const BASE_URL = process.env.REACT_APP_API_BASE_URL_RENDER

const testService = {
  /**
   * Envía las respuestas del test A (test Cleaver) al backend
   * 
   * @param {string} candidatoId - ID del candidato
   * @param {Array} respuestas - Array con las respuestas del test Cleaver
   * @returns {Promise<Object>} Respuesta del servidor
   * @throws {Object} Error con mensaje descriptivo si falla el envío
   * 
   * @example
   * const respuestas = [
   *   "Ecuánime", "Gentil", "Humilde", "Original", "Agresivo"
   * ];
   * const result = await testService.enviarRespuestasTestA('67ce718e-b668-4968-930c-fccc72756190', respuestas);
   */
  async enviarRespuestasTestA(candidatoId, respuestas) {
    try {
      if (!candidatoId) {
        throw { message: 'El ID del candidato es requerido' };
      }

      if (!Array.isArray(respuestas) || respuestas.length === 0) {
        throw { message: 'Las respuestas son requeridas y deben ser un array válido' };
      }

      const requestBody = {
        id_candidato: candidatoId,
        answers: respuestas
      };

      const response = await fetch(`${BASE_URL}/api/cleaver/resultado`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          try {
            errorData = { message: await response.text() };
          } catch (e2) {
            errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
          }
        }

        console.error('Error enviando respuestas del test A (Cleaver):', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorData: errorData
        });

        throw { 
          message: errorData.message || errorData.detail || 'Error al enviar las respuestas del test A (Cleaver)',
          status: response.status,
          details: errorData
        };
      }

      const data = await response.json();
      return data;

    } catch (error) {
      console.error('Error en enviarRespuestasTestA:', error);
      throw error.message ? error : { message: 'Error en el servidor al enviar respuestas del test A (Cleaver)' };
    }
  },

  /**
   * Envía las respuestas del test B (test de valores) al backend
   * 
   * @param {string} candidatoId - ID del candidato
   * @param {Array} respuestas - Array con las respuestas del test formateadas
   * @returns {Promise<Object>} Respuesta del servidor
   * @throws {Object} Error con mensaje descriptivo si falla el envío
   * 
   * @example
   * const respuestas = [
   *   { numero_pregunta: 1, opcion: "A", valor_asignado: 4 },
   *   { numero_pregunta: 1, opcion: "B", valor_asignado: 3 },
   * ];
   * const result = await testValoresService.enviarRespuestasTestB('123', respuestas);
   */
  async enviarRespuestasTestB(candidatoId, respuestas) {
    try {
      if (!candidatoId) {
        throw { message: 'El ID del candidato es requerido' };
      }

      if (!Array.isArray(respuestas) || respuestas.length === 0) {
        throw { message: 'Las respuestas son requeridas y deben ser un array válido' };
      }


      const response = await fetch(`${BASE_URL}/test-valores/respuestas/${candidatoId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(respuestas)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          try {
            errorData = { message: await response.text() };
          } catch (e2) {
            errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
          }
        }

        console.error('Error enviando respuestas del test B:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorData: errorData
        });

        throw { 
          message: errorData.message || errorData.detail || 'Error al enviar las respuestas del test B',
          status: response.status,
          details: errorData
        };
      }

      const data = await response.json();

      return data;

    } catch (error) {
      console.error('Error en enviarRespuestasTestB:', error);
      throw error.message ? error : { message: 'Error en el servidor al enviar respuestas del test B' };
    }
  },

  /**
   * Formatea las respuestas del test B según el formato esperado por el backend
   * 
   * @param {Object} respuestasRaw - Respuestas en formato raw del frontend
   * @returns {Array} Array formateado según el esquema del backend
   * 
   * @example
   * const respuestasRaw = {
   *   1: { A: 4, B: 3, C: 2, D: 1 },
   *   2: { D: 4, C: 3, A: 2, B: 1 }
   * };
   * const formateadas = testValoresService.formatearRespuestasTestB(respuestasRaw);
   */
  formatearRespuestasTestB(respuestasRaw) {
    try {
      const respuestasFormateadas = [];

      for (const [numeroPregunta, opciones] of Object.entries(respuestasRaw)) {
        for (const [opcion, valorAsignado] of Object.entries(opciones)) {
          respuestasFormateadas.push({
            numero_pregunta: parseInt(numeroPregunta),
            opcion: opcion,
            valor_asignado: valorAsignado
          });
        }
      }

      return respuestasFormateadas;
    } catch (error) {
      console.error('Error formateando respuestas del test B:', error);
      throw { message: 'Error al formatear las respuestas del test B' };
    }
  },

  /**
   * Envía las respuestas del test C (test de canciones) al backend
   * 
   * @param {string} candidatoId - ID del candidato
   * @param {Object} respuestas - Objeto con fase_1 y fase_2 del test de canciones
   * @returns {Promise<Object>} Respuesta del servidor
   * @throws {Object} Error con mensaje descriptivo si falla el envío
   * 
   * @example
   * const respuestas = {
   *   fase_1: {
   *     "No Diggity": "no me gusta",
   *     "Lose Yourself": "me gusta"
   *   },
   *   fase_2: {
   *     "No Diggity vs My Sharona": "My Sharona"
   *   }
   * };
   * const result = await testService.enviarRespuestasTestC('123', respuestas);
   */
  async enviarRespuestasTestC(candidatoId, respuestas) {
    try {
      if (!candidatoId) {
        throw { message: 'El ID del candidato es requerido' };
      }

      if (!respuestas || typeof respuestas !== 'object') {
        throw { message: 'Las respuestas son requeridas y deben ser un objeto válido' };
      }


      const response = await fetch(`${BASE_URL}/test-canciones/${candidatoId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(respuestas)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          try {
            errorData = { message: await response.text() };
          } catch (e2) {
            errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
          }
        }

        console.error('Error enviando respuestas del test C:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorData: errorData
        });

        throw { 
          message: errorData.message || errorData.detail || 'Error al enviar las respuestas del test C',
          status: response.status,
          details: errorData
        };
      }

      const data = await response.json();

      return data;

    } catch (error) {
      console.error('Error en enviarRespuestasTestC:', error);
      throw error.message ? error : { message: 'Error en el servidor al enviar respuestas del test C' };
    }
  },

  /**
   * Envía las respuestas del test D (test de dibujos) al backend
   * 
   * @param {string} candidatoId - ID del candidato
   * @param {string} mostPreferred - ID del dibujo más preferido
   * @param {string} secondPreferred - ID del dibujo segundo más preferido
   * @returns {Promise<Object>} Respuesta del servidor
   * @throws {Object} Error con mensaje descriptivo si falla el envío
   * 
   * @example
   * const result = await testService.enviarRespuestasTestD('ca706ce4-bf8f-47ce-8a9f-9cae766cf7a3', '1', '6');
   */
  async enviarRespuestasTestD(candidatoId, mostPreferred, secondPreferred) {
    try {
      if (!candidatoId) {
        throw { message: 'El ID del candidato es requerido' };
      }

      if (!mostPreferred || !secondPreferred) {
        throw { message: 'Las respuestas de dibujos más y segundo más preferidos son requeridas' };
      }

      const requestBody = {
        id_candidato: candidatoId,
        most_preferred: mostPreferred,
        second_preferred: secondPreferred
      };

      const response = await fetch(`${BASE_URL}/drawing/evaluate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          try {
            errorData = { message: await response.text() };
          } catch (e2) {
            errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
          }
        }

        console.error('Error enviando respuestas del test D (dibujos):', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorData: errorData
        });

        throw { 
          message: errorData.message || errorData.detail || 'Error al enviar las respuestas del test D (dibujos)',
          status: response.status,
          details: errorData
        };
      }

      const data = await response.json();
      return data;

    } catch (error) {
      console.error('Error en enviarRespuestasTestD:', error);
      throw error.message ? error : { message: 'Error en el servidor al enviar respuestas del test D (dibujos)' };
    }
  },

  /**
   * Obtiene los resultados del test de valores para un candidato específico
   * 
   * @param {string} candidatoId - ID del candidato
   * @returns {Promise<Object>} Datos del test de valores con la estructura esperada
   * @throws {Object} Error con mensaje descriptivo si falla la consulta
   * 
   * @example
   * const testResults = await testService.obtenerResultadosTestValores('123e4567-e89b-12d3-a456-426614174000');
   */
  async obtenerResultadosTestValores(candidatoId) {
    try {
      if (!candidatoId) {
        throw { message: 'El ID del candidato es requerido' };
      }

      // Check cache first
      const cacheKey = `test-valores-${candidatoId}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const url = `${BASE_URL}/test-valores/respuestas?id_candidato=${candidatoId}`;
      console.log('Making request to:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          try {
            errorData = { message: await response.text() };
          } catch (e2) {
            errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
          }
        }

        console.error('Error obteniendo resultados del test de valores:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorData: errorData
        });

        throw { 
          message: errorData.message || errorData.detail || 'Error al obtener los resultados del test de valores',
          status: response.status,
          details: errorData
        };
      }

      const data = await response.json();
      
      // Verificar si ya viene con la estructura correcta
      if (data.success && data.data) {
        this.setCache(cacheKey, data);
        return data;
      }
      
      // Si viene solo el objeto, envolverlo en la estructura esperada
      const result = {
        success: true,
        data: [data]
      };
      
      // Cache the result
      this.setCache(cacheKey, result);
      return result;

    } catch (error) {
      console.error('Error en obtenerResultadosTestValores:', error);
      throw error.message ? error : { message: 'Error en el servidor al obtener resultados del test de valores' };
    }
  },

  /**
   * Procesa los datos del test de valores para calcular puntajes por categoría
   * 
   * @param {Object} valuesTestData - Datos del test de valores
   * @returns {Object} Puntajes procesados por categoría
   * 
   * @example
   * const processedScores = testService.procesarPuntajesTestValores(testData);
   */
  procesarPuntajesTestValores(valuesTestData) {
    if (!valuesTestData?.success || !valuesTestData?.data?.[0]?.respuestas) {
      return {
        "Principios Éticos": 0,
        "Conformidad Normativa": 0,
        "Apatía Social": 0,
        "Tendencia a la Corrupción": 0
      };
    }

    const respuestas = valuesTestData.data[0].respuestas;
    const categoryScores = {
      "Principios Éticos": 0,
      "Conformidad Normativa": 0,
      "Apatía Social": 0,
      "Tendencia a la Corrupción": 0
    };

    // Mapear nombres de categorías del backend a nombres mostrados en el frontend
    const categoryMapping = {
      "Principios Eticos": "Principios Éticos",
      "Conformidad Normativa": "Conformidad Normativa", 
      "Apatia Social": "Apatía Social",
      "Tendencia a la Corrupcion": "Tendencia a la Corrupción"
    };

    respuestas.forEach(respuesta => {
      const categoryName = categoryMapping[respuesta.categoria] || respuesta.categoria;
      if (categoryScores.hasOwnProperty(categoryName)) {
        categoryScores[categoryName] += respuesta.valor_asignado;
      }
    });

    return categoryScores;
  },

  /**
   * Obtiene los resultados del test de dibujos para un candidato específico
   * 
   * @param {string} candidatoId - ID del candidato
   * @returns {Promise<Object>} Datos del test de dibujos con total_score y classification_description
   * @throws {Object} Error con mensaje descriptivo si falla la consulta
   * 
   * @example
   * const drawingResults = await testService.obtenerResultadosTestDibujos('123e4567-e89b-12d3-a456-426614174000');
   */
  async obtenerResultadosTestDibujos(candidatoId) {
    try {
      if (!candidatoId) {
        throw { message: 'El ID del candidato es requerido' };
      }

      // Check cache first
      const cacheKey = `test-dibujos-${candidatoId}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const url = `${BASE_URL}/drawing/responses?id_candidato=${candidatoId}`;
      console.log('Making request to:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          try {
            errorData = { message: await response.text() };
          } catch (e2) {
            errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
          }
        }

        console.error('Error obteniendo resultados del test de dibujos:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorData: errorData
        });

        throw { 
          message: errorData.message || errorData.detail || 'Error al obtener los resultados del test de dibujos',
          status: response.status,
          details: errorData
        };
      }

      const data = await response.json();
      
      // Cache the result
      this.setCache(cacheKey, data);
      return data;

    } catch (error) {
      console.error('Error en obtenerResultadosTestDibujos:', error);
      throw error.message ? error : { message: 'Error en el servidor al obtener resultados del test de dibujos' };
    }
  },

  /**
   * Obtiene los resultados del test de canciones para un candidato específico
   * 
   * @param {string} candidatoId - ID del candidato
   * @returns {Promise<Object>} Datos del test de canciones con puntaje_total
   * @throws {Object} Error con mensaje descriptivo si falla la consulta
   * 
   * @example
   * const songResults = await testService.obtenerResultadosTestCanciones('123e4567-e89b-12d3-a456-426614174000');
   */
  async obtenerResultadosTestCanciones(candidatoId) {
    try {
      if (!candidatoId) {
        throw { message: 'El ID del candidato es requerido' };
      }

      // Check cache first
      const cacheKey = `test-canciones-${candidatoId}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const url = `${BASE_URL}/test-canciones/respuestas/resumen/${candidatoId}`;
      console.log('Making request to:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          try {
            errorData = { message: await response.text() };
          } catch (e2) {
            errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
          }
        }

        console.error('Error obteniendo resultados del test de canciones:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorData: errorData
        });

        throw { 
          message: errorData.message || errorData.detail || 'Error al obtener los resultados del test de canciones',
          status: response.status,
          details: errorData
        };
      }

      const data = await response.json();
      
      // Cache the result
      this.setCache(cacheKey, data);
      return data;

    } catch (error) {
      console.error('Error en obtenerResultadosTestCanciones:', error);
      throw error.message ? error : { message: 'Error en el servidor al obtener resultados del test de canciones' };
    }
  },

  // Simple cache implementation
  cache: new Map(),
  CACHE_TTL: 5 * 60 * 1000, // 5 minutes

  getFromCache(key) {
    const item = this.cache.get(key);
    if (item && Date.now() - item.timestamp < this.CACHE_TTL) {
      return item.data;
    }
    this.cache.delete(key);
    return null;
  },

  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
    
    // Clean old cache entries periodically
    if (this.cache.size > 100) {
      const now = Date.now();
      for (const [k, v] of this.cache.entries()) {
        if (now - v.timestamp >= this.CACHE_TTL) {
          this.cache.delete(k);
        }
      }
    }
  }
};

export default testService;